import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Sun, Moon, User } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { SUBDOMAIN, subdomainHref } from '../../lib/subdomains.js';

const HamburgerMenuPanel = lazy(() => import('./HamburgerMenuPanel.jsx'));
const SocialDrawerTray = lazy(() => import('./SocialDrawerTray.jsx'));

/* ─── Social drawer ──────────────────────────────────────────────── */

function SocialDrawerBtn({ open, onClick, theme }) {
  const [hovered, setHovered] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pointerMovedRef = useRef(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    const onMove = () => { pointerMovedRef.current = true; };
    window.addEventListener('pointermove', onMove, { once: true });
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('pointermove', onMove);
    };
  }, []);
  const isWexford    = theme === 'wexford';
  const iconColor    = isWexford ? 'var(--text-wexford-heading)' : 'var(--text-nav-warm)';
  const hoverColor   = isWexford ? 'var(--accent-wexford)'       : 'var(--text-nav-warm-hover)';
  const openBg       = isWexford ? 'var(--bg-nav-wexford)'       : 'var(--bg-nav-warm)';
  const hoverBg      = isWexford ? 'rgba(77,159,214,0.12)'       : 'var(--bg-nav-warm-hover)';
  const mutedBorder  = isWexford ? 'var(--accent-wexford)'       : 'var(--text-nav-warm-muted)';

  return (
    <button
      onClick={onClick}
      aria-label={open ? 'Close socials' : 'Open socials'}
      aria-expanded={open}
      onPointerEnter={() => { if (pointerMovedRef.current) setHovered(true); }}
      onPointerLeave={() => setHovered(false)}
      style={{
        background: open ? openBg : (hovered ? hoverBg : 'none'),
        border: `1.5px solid ${(open || hovered) ? mutedBorder : 'transparent'}`,
        /* Square off the bottom when open so the tray attaches flush */
        borderRadius: open ? '8px 8px 0 0' : '8px',
        /* Hide the bottom border seam — tray's top border takes over */
        borderBottom: open ? `1.5px solid ${openBg}` : undefined,
        cursor: 'pointer',
        color: (open || hovered) ? hoverColor : iconColor,
        padding: '4px 0',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 101,
        transition: mounted ? 'color 150ms, background 150ms, border-color 150ms, border-radius 120ms' : 'none',
      }}
    >
      <div style={{ position: 'relative', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          position: 'absolute',
          transition: 'opacity 150ms, transform 150ms',
          opacity: open ? 0 : 1,
          transform: open ? 'rotate(90deg) scale(0.6)' : 'rotate(0deg) scale(1)',
        }}>
          <User size={18} />
        </div>
        <div style={{
          position: 'absolute',
          transition: 'opacity 150ms, transform 150ms',
          opacity: open ? 1 : 0,
          transform: open ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.6)',
        }}>
          <X size={16} />
        </div>
      </div>
    </button>
  );
}

export default function SiteNavbar({ theme = 'warm' }) {
  const [open, setOpen]               = useState(false);
  const [menuHasOpened, setMenuHasOpened] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);
  const socialsRef                    = useRef(null);
  const location = useLocation();
  const { darkMode, toggleDarkMode } = useTheme();

  const isWexford = theme === 'wexford';

  const navBg       = isWexford ? 'var(--bg-nav-wexford)'        : 'var(--bg-nav-warm)';
  const border      = isWexford ? 'var(--border-wexford)'        : 'var(--border-warm)';
  const shadow      = isWexford ? 'var(--shadow-wexford)'        : '0 1px 8px rgba(0,0,0,0.25)';
  const iconColor   = isWexford ? 'var(--text-wexford-heading)'  : 'var(--text-nav-warm)';
  const hoverColor  = isWexford ? 'var(--accent-wexford)'        : 'var(--text-nav-warm-hover)';

  const handleMenuToggle = () => {
    if (!open) setMenuHasOpened(true);
    setOpen(v => !v);
  };

  // Close menus on route change
  // Resetting menu state on navigation is intentional here.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setOpen(false); setSocialsOpen(false); }, [location.pathname]);

  // Hide the floating presence card while the drawer is open (it's redundant
  // with the presence footer inside the drawer and would overlap it visually).
  useEffect(() => {
    document.body.classList.toggle('nav-open', open);
    return () => document.body.classList.remove('nav-open');
  }, [open]);

  // Close socials drawer on outside click
  useEffect(() => {
    function handleDown(e) {
      if (socialsRef.current && !socialsRef.current.contains(e.target)) {
        setSocialsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, []);

  // Home link: cross-origin anchor on wexford subdomain, SPA Link everywhere else
  const homeLink = SUBDOMAIN === 'wexford' ? (
    <a
      href={subdomainHref(null)}
      aria-label="Home"
      style={{ display: 'flex', alignItems: 'center', color: iconColor, textDecoration: 'none', transition: 'color 150ms ease' }}
      onPointerEnter={e => { e.currentTarget.style.color = hoverColor; }}
      onPointerLeave={e => { e.currentTarget.style.color = iconColor; }}
    >
      <Home size={20} />
    </a>
  ) : (
    <Link
      to="/"
      aria-label="Home"
      style={{ display: 'flex', alignItems: 'center', color: iconColor, textDecoration: 'none', transition: 'color 150ms ease' }}
      onPointerEnter={e => { e.currentTarget.style.color = hoverColor; }}
      onPointerLeave={e => { e.currentTarget.style.color = iconColor; }}
    >
      <Home size={20} />
    </Link>
  );

  return (
    <header className="site-navbar" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>
      {/* Main bar */}
      <div
        style={{
          height: '60px',
          background: navBg,
          borderBottom: `1px solid ${border}`,
          boxShadow: shadow,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 clamp(1rem, 4vw, 2.5rem)',
        }}
      >
        {/* Left — hamburger + home icon */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: '72px' }}>
          <button
            className="hamburger-btn"
            onClick={handleMenuToggle}
            aria-label={open ? 'Close menu' : 'Open menu'}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: iconColor,
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 150ms ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = hoverColor; }}
            onMouseLeave={e => { e.currentTarget.style.color = iconColor; }}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          {homeLink}
        </div>

        {/* Right — Ask Claude + socials drawer + day/night toggle */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.4rem', minWidth: '90px' }}>
          {/* Social drawer anchor */}
          <div ref={socialsRef} style={{ position: 'relative', width: '45px' }}>
            <SocialDrawerBtn
              open={socialsOpen}
              onClick={() => setSocialsOpen(v => !v)}
              theme={theme}
            />
            {socialsOpen && (
              <Suspense fallback={null}>
                <SocialDrawerTray theme={theme} />
              </Suspense>
            )}
          </div>

          {/* Thin divider */}
          <div style={{ width: '1px', height: '20px', background: isWexford ? 'var(--border-wexford)' : 'var(--border-warm)', flexShrink: 0 }} />

          {/* Dark mode toggle */}
          <button
            onClick={toggleDarkMode}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: iconColor,
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 150ms ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = hoverColor; }}
            onMouseLeave={e => { e.currentTarget.style.color = iconColor; }}
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>
      </div>

      {/* Responsive menu panel — drawer on mobile, accordion on desktop */}
      {(open || menuHasOpened) && (
        <Suspense fallback={null}>
          <HamburgerMenuPanel open={open} onClose={() => setOpen(false)} theme={theme} />
        </Suspense>
      )}
    </header>
  );
}
